import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useState } from 'react';
import useSWR from 'swr';
import { Request } from '../../../../../../../typings/Request.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { useCopyToClipboard } from '../../../../utils/copy';
import { getRejectionCauseBreakdown } from '../../../../utils/rejection-causes';
import Alert from '../../../common/base/Alert';
import Badge from '../../../common/base/Badge';
import Button from '../../../common/base/Button';
import Divider from '../../../common/base/Divider';
import Link from '../../../common/base/Link';
import Loading from '../../../common/base/Loading';
import Tabs from '../../../common/base/Tabs';
import Text from '../../../common/base/Text';
import { BodyCollapsibleSection } from '../../../common/Data/Body';
import { HeadersCollapsibleSection } from '../../../common/Data/Headers';
import { QueryCollapsibleSection } from '../../../common/Data/Query';
import DropdownMenu from '../../../common/DropdownMenu';
import { Div } from '../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useCopyAsCurl from '../../../hooks/useCopyAsCurl';
import useRequestActions from '../../../hooks/useRequestActions';
import { StyledViewNavHeader, StyledViewNavSecondary, StyledViewSidebar, StyledViewSidebarActions, StyledViewSidebarFooter, StyledViewSidebarNav, StyledViewSidebarScrollableSection, ViewSidebarTitle } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../DashboardContext';
import { RequestMetadata } from './RequestMetadata';
const RequestSidebar: React.FC<{
  request_id: string;
  list_request?: Request;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}> = t0 => {
  const $ = _c(97);
  const {
    request_id,
    list_request,
    onClose,
    onPrev,
    onNext
  } = t0;
  const copyToClipboard = useCopyToClipboard();
  const {
    openActionDialog
  } = useRequestActions();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription,
    organization
  } = useContext(DashboardContext);
  const [active_tab, setActiveTab] = useState("request");
  let t1;
  if ($[0] !== request_id) {
    t1 = APIMethodKeys.requests.get(request_id);
    $[0] = request_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== HookdeckAPI.requests || $[3] !== request_id) {
    t2 = () => HookdeckAPI.requests.get(request_id);
    $[2] = HookdeckAPI.requests;
    $[3] = request_id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: request,
    mutate
  } = useSWR(t1, t2);
  let t3;
  if ($[5] !== request) {
    t3 = request && APIMethodKeys.sources.get(request.source_id);
    $[5] = request;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== HookdeckAPI.sources || $[8] !== request?.source_id) {
    t4 = () => HookdeckAPI.sources.get(request?.source_id as string);
    $[7] = HookdeckAPI.sources;
    $[8] = request?.source_id;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data: source
  } = useSWR(t3, t4);
  let t5;
  if ($[10] !== list_request || $[11] !== mutate || $[12] !== request) {
    t5 = () => {
      if (list_request && request && list_request.id === request.id) {
        mutate({
          ...request,
          ...list_request
        }, false);
      }
    };
    $[10] = list_request;
    $[11] = mutate;
    $[12] = request;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== list_request) {
    t6 = [list_request];
    $[14] = list_request;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[16] !== onClose || $[17] !== onNext || $[18] !== onPrev) {
    t7 = () => {
      const handle_keydown = event => {
        if (event.key === "ArrowUp" && onPrev) {
          event.preventDefault();
          onPrev();
        } else {
          if (event.key === "ArrowDown" && onNext) {
            event.preventDefault();
            onNext();
          } else {
            if (event.key === "Escape") {
              event.preventDefault();
              onClose();
            }
          }
        }
      };
      window.addEventListener("keydown", handle_keydown);
      return () => {
        window.removeEventListener("keydown", handle_keydown);
      };
    };
    t8 = [onPrev, onNext, onClose];
    $[16] = onClose;
    $[17] = onNext;
    $[18] = onPrev;
    $[19] = t7;
    $[20] = t8;
  } else {
    t7 = $[19];
    t8 = $[20];
  }
  useEffect(t7, t8);
  let t9;
  if ($[21] !== request || $[22] !== source) {
    t9 = {
      request,
      source
    };
    $[21] = request;
    $[22] = source;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const copyAsCurl = useCopyAsCurl(t9);
  let t10;
  if ($[24] !== request || $[25] !== source) {
    t10 = request?.rejection_cause && source && getRejectionCauseBreakdown(request.rejection_cause)({
      request,
      source
    });
    $[24] = request;
    $[25] = source;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const rejection_cause_breakdown = t10;
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [{
      key: "request",
      label: "Request"
    }, {
      key: "metadata",
      label: "Metadata"
    }];
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const tabs = t11;
  const request_url = `${source?.url}${request?.data?.path}`;
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      align: "center",
      gap: 3
    };
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== request_id) {
    t13 = <ViewSidebarTitle title={request_id} />;
    $[29] = request_id;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== rejection_cause_breakdown || $[32] !== request) {
    t14 = request && (rejection_cause_breakdown ? <Badge danger={true} subtle={true}>Rejected</Badge> : <Badge success={true} subtle={true}>Accepted</Badge>);
    $[31] = rejection_cause_breakdown;
    $[32] = request;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t13 || $[35] !== t14) {
    t15 = <Div flex={t12}>{t13}{t14}</Div>;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const t16 = !request;
  let t17;
  if ($[37] !== openActionDialog || $[38] !== request) {
    t17 = () => request && openActionDialog(request);
    $[37] = openActionDialog;
    $[38] = request;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t16 || $[41] !== t17) {
    t18 = {
      label: "Replay",
      icon: "replay",
      disabled: t16,
      tooltip: "Re-ingest this request through the pipeline as a brand-new request, targeting a chosen set of connections.",
      onClick: t17
    };
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const t19 = !request;
  let t20;
  if ($[43] !== copyAsCurl || $[44] !== t19) {
    t20 = {
      label: "Copy as cURL",
      icon: "copy",
      disabled: t19,
      tooltip: "Copy request as cURL",
      onClick: copyAsCurl
    };
    $[43] = copyAsCurl;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t18 || $[47] !== t20) {
    t21 = <DropdownMenu ghost={true} icon="horizontal_more" placement="bottom-end" options={[t18, t20]} />;
    $[46] = t18;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Divider vertical={true} m={{
      y: 1,
      x: 2
    }} />;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const t23 = `/requests/${request_id}`;
  let t24;
  if ($[50] !== t23) {
    t24 = <Button ghost={true} icon="open" to={t23} />;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== onClose) {
    t25 = <Button ghost={true} icon="close" onClick={onClose} />;
    $[52] = onClose;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t21 || $[55] !== t24 || $[56] !== t25) {
    t26 = <StyledViewSidebarActions>{t21}{t22}{t24}{t25}</StyledViewSidebarActions>;
    $[54] = t21;
    $[55] = t24;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t15 || $[59] !== t26) {
    t27 = <StyledViewNavHeader>{t15}{t26}</StyledViewNavHeader>;
    $[58] = t15;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  const nav_header = t27;
  if (!source || !request) {
    let t28;
    if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <Div flex={{
        grow: true,
        align: "center",
        justify: "center"
      }}><Loading /></Div>;
      $[61] = t28;
    } else {
      t28 = $[61];
    }
    let t29;
    if ($[62] !== nav_header) {
      t29 = <StyledViewSidebar>{nav_header}{t28}</StyledViewSidebar>;
      $[62] = nav_header;
      $[63] = t29;
    } else {
      t29 = $[63];
    }
    return t29;
  }
  let t28;
  if ($[64] !== rejection_cause_breakdown) {
    t28 = rejection_cause_breakdown && <Alert neutral={true} inline={true} title={rejection_cause_breakdown.label} m={{
      b: 4,
      t: 2
    }}>{rejection_cause_breakdown.text}</Alert>;
    $[64] = rejection_cause_breakdown;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = tab => setActiveTab(tab);
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== active_tab) {
    t30 = <Tabs active_tab={active_tab} border={false} onTabSelected={t29} tabs={tabs} />;
    $[67] = active_tab;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t28 || $[70] !== t30) {
    t31 = <StyledViewNavSecondary><Div>{t28}{t30}</Div></StyledViewNavSecondary>;
    $[69] = t28;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== nav_header || $[73] !== t31) {
    t32 = <StyledViewSidebarNav>{nav_header}{t31}</StyledViewSidebarNav>;
    $[72] = nav_header;
    $[73] = t31;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== active_tab || $[76] !== copyToClipboard || $[77] !== organization || $[78] !== request || $[79] !== request_id || $[80] !== request_url || $[81] !== subscription) {
    t33 = active_tab === "request" && <Div>{!request.data ? <Div p={{
        x: 6,
        y: 6
      }}><Text muted={true} as="p">This request is past your archival period of {subscription.retention_days} days, the request data is no longer available.</Text>{!organization && <Link to="/settings/organization/plans?highlight=retention_days" icon="upgrade">Upgrade Plan</Link>}</Div> : <><Div flex={{
          align: "center",
          gap: 2,
          justify: "space-between"
        }} m={{
          b: 0
        }} p={{
          x: 6,
          y: 3
        }}><Badge neutral={true}>{request.data.headers["x-hookdeck-original-method"] || "POST"}</Badge><Text mono={true} as="span" ellipsis={true} size="s" w={100}>{request_url}</Text><Button ghost={true} icon="copy" muted={true} onClick={() => copyToClipboard(request_url)} /></Div><Divider /><HeadersCollapsibleSection headers={request.data.headers} embedded={true} context_menu={true} />{request.data.query && <QueryCollapsibleSection query={request.data.query} parsed_query={request.data.parsed_query} embedded={true} context_menu={true} />}{(request.data.body || request.data.body_format === "binary") && <BodyCollapsibleSection body={request.data.body} body_format={request.data.body_format} headers={request.data.headers} embedded={true} type="request" id={request_id} context_menu={true} />}</>}</Div>;
    $[75] = active_tab;
    $[76] = copyToClipboard;
    $[77] = organization;
    $[78] = request;
    $[79] = request_id;
    $[80] = request_url;
    $[81] = subscription;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== active_tab || $[84] !== request || $[85] !== source) {
    t34 = active_tab === "metadata" && <Div p={{
      x: 6,
      b: 6
    }}><RequestMetadata request={request} source={source} embedded={true} /></Div>;
    $[83] = active_tab;
    $[84] = request;
    $[85] = source;
    $[86] = t34;
  } else {
    t34 = $[86];
  }
  let t35;
  if ($[87] !== t33 || $[88] !== t34) {
    t35 = <StyledViewSidebarScrollableSection>{t33}{t34}</StyledViewSidebarScrollableSection>;
    $[87] = t33;
    $[88] = t34;
    $[89] = t35;
  } else {
    t35 = $[89];
  }
  let t36;
  if ($[90] !== onNext || $[91] !== onPrev) {
    t36 = (onPrev || onNext) && <StyledViewSidebarFooter><Div w={100} flex={{
        align: "center",
        gap: 2,
        justify: "flex-end"
      }}><Button outline={true} icon="chevron_up" onClick={onPrev} /><Button outline={true} icon="chevron_down" onClick={onNext} /></Div></StyledViewSidebarFooter>;
    $[90] = onNext;
    $[91] = onPrev;
    $[92] = t36;
  } else {
    t36 = $[92];
  }
  let t37;
  if ($[93] !== t32 || $[94] !== t35 || $[95] !== t36) {
    t37 = <StyledViewSidebar>{t32}{t35}{t36}</StyledViewSidebar>;
    $[93] = t32;
    $[94] = t35;
    $[95] = t36;
    $[96] = t37;
  } else {
    t37 = $[96];
  }
  return t37;
};
export default RequestSidebar;